/**
 * VideoPreviewPlayer -- full video player UI for VideoGeneratorNode.
 *
 * Features:
 * - Hover play/pause/resume (pause on leave, resume on re-enter)
 * - Single-play-per-hover: plays through at most once, then stops on last frame
 * - Bottom bar: play/pause button, current time, scrub bar, total duration
 * - Top-left volume toggle (muted by default)
 * - Optional expand/download overlay buttons (top-right, hover-visible)
 * - Exposes video element ref via forwardRef for external control (Play All)
 */

import {
  memo,
  useRef,
  useState,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
  type MouseEvent as ReactMouseEvent,
} from 'react'

/** Format seconds as M:SS (e.g. 65 -> "1:05") */
export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/** Imperative handle exposed by VideoPreviewPlayer via forwardRef */
export interface VideoPreviewPlayerHandle {
  play: () => void
  pause: () => void
  readonly videoElement: HTMLVideoElement | null
}

export interface VideoPreviewPlayerProps {
  videoUrl: string
  isHovering: boolean
  /** Show expand button in top-right overlay */
  showExpand?: boolean
  /** Show download button in top-right overlay */
  showDownload?: boolean
  /** Called when expand button is clicked */
  onExpand?: () => void
  /** Called when download button is clicked */
  onDownload?: () => void
  /** Called when metadata exposes natural video dimensions */
  onLoadedDimensions?: (width: number, height: number) => void
  /** Called when the video reaches the end */
  onEnded?: () => void
}

export const VideoPreviewPlayer = memo(forwardRef<VideoPreviewPlayerHandle, VideoPreviewPlayerProps>(
  function VideoPreviewPlayer(
    {
      videoUrl,
      isHovering,
      showExpand,
      showDownload,
      onExpand,
      onDownload,
      onLoadedDimensions,
      onEnded,
    },
    ref,
  ) {
  const videoRef = useRef<HTMLVideoElement>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isMuted, setIsMuted] = useState(true)
  const [hasCompletedPlayback, setHasCompletedPlayback] = useState(false)

  const manualPauseRef = useRef(false)
  const isDraggingRef = useRef(false)

  /* -- Expose imperative handle for external control -- */
  useImperativeHandle(ref, () => ({
    play: () => {
      const video = videoRef.current
      if (!video) return
      if (hasCompletedPlayback) {
        video.currentTime = 0
        setHasCompletedPlayback(false)
      }
      video.play().catch(() => {})
      setIsPlaying(true)
    },
    pause: () => {
      const video = videoRef.current
      if (!video) return
      video.pause()
      setIsPlaying(false)
    },
    get videoElement() {
      return videoRef.current
    },
  }), [hasCompletedPlayback])

  /* -- Hover play/pause/resume -- */
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    if (isHovering) {
      if (hasCompletedPlayback) {
        video.currentTime = 0
        setHasCompletedPlayback(false)
      }
      manualPauseRef.current = false
      video.play().catch(() => {})
      setIsPlaying(true)
    } else {
      video.pause()
      setIsPlaying(false)
    }
  }, [isHovering]) // eslint-disable-line react-hooks/exhaustive-deps

  /* -- Video event handlers -- */
  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current
    if (!video || isDraggingRef.current) return
    setCurrentTime(video.currentTime)
  }, [])

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    setDuration(video.duration)
    onLoadedDimensions?.(video.videoWidth, video.videoHeight)
  }, [onLoadedDimensions])

  const handleEnded = useCallback(() => {
    setIsPlaying(false)
    setHasCompletedPlayback(true)
    onEnded?.()
  }, [onEnded])

  /* -- Play/Pause toggle -- */
  const togglePlayPause = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation()
      const video = videoRef.current
      if (!video) return

      if (isPlaying) {
        video.pause()
        setIsPlaying(false)
        manualPauseRef.current = true
      } else {
        if (hasCompletedPlayback) {
          video.currentTime = 0
          setHasCompletedPlayback(false)
        }
        video.play().catch(() => {})
        setIsPlaying(true)
        manualPauseRef.current = false
      }
    },
    [isPlaying, hasCompletedPlayback]
  )

  /* -- Volume toggle -- */
  const toggleMute = useCallback((e: ReactMouseEvent) => {
    e.stopPropagation()
    const video = videoRef.current
    if (!video) return
    video.muted = !video.muted
    setIsMuted(video.muted)
  }, [])

  /* -- Scrub bar -- */
  const scrubBarRef = useRef<HTMLDivElement>(null)

  const seekToPosition = useCallback((clientX: number) => {
    const video = videoRef.current
    const bar = scrubBarRef.current
    if (
      !video ||
      !bar ||
      !Number.isFinite(video.duration) ||
      video.duration <= 0
    )
      return

    const rect = bar.getBoundingClientRect()
    const ratio = Math.max(
      0,
      Math.min(1, (clientX - rect.left) / rect.width)
    )
    const newTime = ratio * video.duration
    video.currentTime = newTime
    setCurrentTime(newTime)

    if (newTime >= video.duration) {
      setHasCompletedPlayback(true)
      setIsPlaying(false)
      video.pause()
    }
  }, [])

  const handleScrubClick = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation()
      seekToPosition(e.clientX)
    },
    [seekToPosition]
  )

  const handleScrubDragStart = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      isDraggingRef.current = true

      const wasPaused = videoRef.current?.paused ?? true
      videoRef.current?.pause()

      seekToPosition(e.clientX)

      const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
        seekToPosition(moveEvent.clientX)
      }

      const handleMouseUp = () => {
        isDraggingRef.current = false
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        if (!wasPaused && !hasCompletedPlayback) {
          videoRef.current?.play().catch(() => {})
          setIsPlaying(true)
        }
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [seekToPosition, hasCompletedPlayback]
  )

  const progress = duration > 0 ? currentTime / duration : 0

  const hasOverlayButtons = showExpand || showDownload

  return (
    <div className="relative w-full h-full" data-testid="video-preview-player">
      <video
        ref={videoRef}
        src={videoUrl}
        className="w-full h-full object-cover"
        muted={isMuted}
        playsInline
        // Default ``preload`` is ``"auto"`` (full download). With 9
        // storyboard clip nodes mounted at once that downloads ~90 MB
        // of video on canvas load and decodes them all in parallel —
        // canvas goes unresponsive for ~5-10 s and chews memory. Only
        // load the file header until the user actually interacts: the
        // VideoPreviewPlayer's play button flips this to autoload on
        // demand and the browser fetches the bytes lazily.
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />

      {/* Volume icon -- top-left, visible on hover */}
      <button
        className={`absolute top-2 left-2 w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition-all z-10 ${
          isHovering
            ? 'opacity-100'
            : 'opacity-0 pointer-events-none'
        }`}
        onClick={toggleMute}
        title={isMuted ? 'Unmute' : 'Mute'}
        data-testid="video-volume-toggle"
      >
        {isMuted ? (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
            />
          </svg>
        ) : (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
            />
          </svg>
        )}
      </button>

      {/* Expand/Download overlay buttons -- top-right, visible on hover */}
      {hasOverlayButtons && (
        <div
          className={`absolute top-2 right-2 flex gap-1 z-10 transition-opacity ${
            isHovering ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          data-testid="video-overlay-buttons"
        >
          {showExpand && onExpand && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onExpand()
              }}
              className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
              title="Expand"
              data-testid="video-expand-btn"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4"
                />
              </svg>
            </button>
          )}
          {showDownload && onDownload && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onDownload()
              }}
              className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
              title="Download"
              data-testid="video-download-btn"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Bottom bar -- visible on hover */}
      <div
        className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5 flex items-center gap-1.5 transition-all ${
          isHovering
            ? 'opacity-100'
            : 'opacity-0 pointer-events-none'
        }`}
        data-testid="video-player-controls"
      >
        {/* Play/Pause button */}
        <button
          className="w-6 h-6 flex items-center justify-center text-foreground hover:text-white transition-colors flex-shrink-0"
          onClick={togglePlayPause}
          title={isPlaying ? 'Pause' : 'Play'}
          data-testid="video-play-pause-btn"
        >
          {isPlaying ? (
            <svg
              className="w-4 h-4"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg
              className="w-4 h-4"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        {/* Current time */}
        <span
          className="text-[10px] text-text-secondary tabular-nums flex-shrink-0 select-none"
          data-testid="video-current-time"
        >
          {formatTime(currentTime)}
        </span>

        {/* Scrub bar */}
        <div
          ref={scrubBarRef}
          className="flex-1 h-5 flex items-center cursor-pointer group/scrub"
          onClick={handleScrubClick}
          onMouseDown={handleScrubDragStart}
          data-testid="video-scrub-bar"
        >
          <div className="w-full h-1 bg-white/20 rounded-full relative group-hover/scrub:h-1.5 transition-all">
            <div
              className="absolute top-0 left-0 h-full bg-white/70 rounded-full"
              style={{ width: `${progress * 100}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md opacity-0 group-hover/scrub:opacity-100 transition-opacity"
              style={{
                left: `${progress * 100}%`,
                transform: `translateX(-50%) translateY(-50%)`,
              }}
              data-testid="video-scrub-playhead"
            />
          </div>
        </div>

        {/* Total duration */}
        <span
          className="text-[10px] text-text-secondary tabular-nums flex-shrink-0 select-none"
          data-testid="video-duration"
        >
          {formatTime(duration)}
        </span>
      </div>
    </div>
  )
}))

VideoPreviewPlayer.displayName = 'VideoPreviewPlayer'
